import type { WorldSnapshot } from "../simulation/snapshot";

export type HudPrimary = "outcome" | "alert" | "objective" | "message" | "none";

export type HudPresentationState = {
  showCombatHud: boolean;
  primary: HudPrimary;
  showObjectiveDetail: boolean;
  showCarrierNavigation: boolean;
  showMessage: boolean;
};

type HudStateInput = Pick<
  WorldSnapshot,
  | "phase"
  | "paused"
  | "runSummary"
  | "incursionActive"
  | "bossBanner"
  | "carrierNav"
  | "message"
  | "runProgress"
>;

/** Pure presentation priority resolver; it never changes simulation state. */
export function resolveHudPresentation(input: HudStateInput): HudPresentationState {
  const inRun =
    input.phase === "launching" ||
    input.phase === "expedition" ||
    input.phase === "extracting" ||
    input.phase === "onboarding";
  const skillPick =
    input.runProgress.unspentSkillPoints > 0 && input.runProgress.skillPickOffers.length > 0;
  const outcome = input.phase === "dead" || input.phase === "extracted" || input.runSummary != null;
  const showCombatHud = inRun && !input.paused && !skillPick && !outcome;
  if (outcome) {
    return {
      showCombatHud: false,
      primary: "outcome",
      showObjectiveDetail: false,
      showCarrierNavigation: false,
      showMessage: false,
    };
  }
  if (!showCombatHud) {
    return {
      showCombatHud: false,
      primary: "none",
      showObjectiveDetail: false,
      showCarrierNavigation: false,
      showMessage: false,
    };
  }
  const alert = input.incursionActive || Boolean(input.bossBanner && input.bossBanner.timer > 0);
  if (alert) {
    return {
      showCombatHud: true,
      primary: "alert",
      showObjectiveDetail: true,
      showCarrierNavigation: Boolean(input.carrierNav?.active),
      showMessage: false,
    };
  }
  // Extraction and ordinary navigation both use the same concise objective card.
  const hasObjective = input.phase === "extracting" || inRun;
  if (hasObjective) {
    return {
      showCombatHud: true,
      primary: "objective",
      showObjectiveDetail: true,
      showCarrierNavigation: Boolean(input.carrierNav?.active),
      showMessage: false,
    };
  }
  return {
    showCombatHud: true,
    primary: input.message ? "message" : "none",
    showObjectiveDetail: false,
    showCarrierNavigation: false,
    showMessage: Boolean(input.message),
  };
}
